import React from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { SkeletonText } from '../../shared/Skeleton';

// A compact row for a permission-gated block (billing / effort). Four states that
// never collapse into each other: loading → skeleton, error → "Currently
// unavailable", loaded + null → a QUIET "no access" (the least loud thing on the
// page, never a warning; null IS the permission signal), loaded + value → the
// row says the panel is pending.
//
// The null is only meaningful once fn_client_context has resolved — while it is in
// flight or failed, `value` is also null and claiming "no access" would be a lie.
const LockIcon = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/**
 * @param {object} props
 * @param {string} props.label       "Billing" | "Effort"
 * @param {string} props.component   Named in the pending line: "Arrives with the {component}."
 * @param {boolean} [props.loading]
 * @param {*} [props.error]
 * @param {*} [props.value]          fn_client_context[billing|effort]; null = no permission.
 */
export default function GatedRow({ label, component, loading, error, value }) {
  const gated = !loading && !error && value == null;
  return (
    <PanelErrorBoundary label={label}>
      <section
        aria-label={label}
        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900"
      >
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</h3>
        {loading ? (
          <div className="w-24" role="status" aria-label="Loading">
            <SkeletonText lines={1} lineClass="h-4" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : error ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Currently unavailable.</p>
        ) : gated ? (
          <p className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <LockIcon /> You don't have access to {label.toLowerCase()}.
          </p>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">Arrives with the {component}.</p>
        )}
      </section>
    </PanelErrorBoundary>
  );
}
